import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import Url from 'url-parse';

import { CredentialsPromptComponent } from '@pwa/component/credentials-prompt/credentials-prompt.component';

import { KvsService } from './kvs.service';
import { LoaderService } from './loader.service';

const DEFAULT_TIMEOUT = 5000;

interface ExtendedRequestInit extends RequestInit {
    timeout?: number;
    loaderDelay?: number;
}

@Injectable({ providedIn: 'root' })
export class FetchService {
    constructor(
        private loaderService: LoaderService,
        private kvsService: KvsService,
        private modalController: ModalController,
    ) {}

    async fetch(input: RequestInfo, init?: ExtendedRequestInit): Promise<Response> {
        const loaderDelay = init?.loaderDelay ?? 0;
        const timeout = init?.timeout === undefined ? DEFAULT_TIMEOUT : init?.timeout;
        const urlParsed = new Url(typeof input === 'string' ? input : input.url);

        let authorizationRequired = false;

        return this.loaderService.showWhile(
            async () => {
                while (true) {
                    const auth = this.kvsService.kvs.credentials[urlParsed.origin];

                    const abortContrtoller = new AbortController();
                    const fetchTimeout = window.setTimeout(() => abortContrtoller.abort(), timeout);

                    try {
                        const response = await fetch(input, {
                            ...this.requestInit(init),
                            signal: abortContrtoller.signal,
                            headers: {
                                ...(init?.headers || {}),
                                ...(authorizationRequired && auth
                                    ? { Authorization: 'Basic ' + btoa(`${auth.username}:${auth.password}`) }
                                    : {}),
                            },
                        });

                        clearTimeout(fetchTimeout);

                        if (response.status === 401) {
                            if (!authorizationRequired && auth) {
                                authorizationRequired = true;
                                continue;
                            }

                            authorizationRequired = true;

                            if (await this.authenticate(urlParsed.origin)) continue;
                        }

                        return response;
                    } catch (error) {
                        clearTimeout(fetchTimeout);
                        throw error;
                    }
                }
            },
            'Loading...',
            loaderDelay,
        );
    }

    private requestInit(init?: ExtendedRequestInit): RequestInit | undefined {
        if (!init) return undefined;

        const { timeout, loaderDelay, ...rest } = init;

        return rest;
    }

    private async authenticate(origin: string): Promise<boolean> {
        const confirmed = await new Promise<boolean>((resolve) =>
            this.modalController
                .create({
                    component: CredentialsPromptComponent,
                    backdropDismiss: false,
                    componentProps: {
                        origin,
                        onContinue: () => resolve(true),
                        onCancel: () => resolve(false),
                    },
                })
                .then((modal) => modal.present()),
        );

        void this.modalController.dismiss();

        return confirmed;
    }
}
